import { Paginated } from "@feathersjs/feathers/lib";
import { authStore, rootStore, routingStore } from "app";
import { paramsForServer } from "feathers-hooks-common";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { computedFn } from "mobx-utils";
import { Pages } from "routes";
import { 
  DataStatus,
  DataType,
  ICurriculum,
  ICourse,
  IFile,
  IProgram,
  IProgramCourse,
  IProgress,
  IStep,
  Services,
  StepType,
  UserType,
  ICourseUser 
} from "types";
import client from "../feathers";
import i18next from "../i18n";
import { Curriculum, File, Step } from "./";

export class Course extends Curriculum<ICourse> implements ICourse {
  public static readonly last_step_session_storage_key = "bgi_last_step";

  category?:               string      = null;
  steps :                  Step[]      = [];
  documents?:              File[]      = [];
  curStep :                Step        = null;
  athen_id :               string      = null;
  notes?:                  string      = null;
  rating?:                 number      = null;
  rating_last_updated_at:  Date | null = null;
  comment                              = "";

  course_progress: IProgress[] = [];

  private steps_loaded = false;

  constructor(data: ICourse) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Course, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      category:               observable,
      steps:                  observable,
      curStep:                observable,
      athen_id:               observable,
      notes:                  observable,
      documents:              observable,
      course_progress:        observable,
      rating:                 observable,
      rating_last_updated_at: observable,
      comment:                observable,

      getStepMap:       computed,
      getChapters:      computed,
      getCurStep:       computed,
      getDocuments:     computed,
      hasFinalQuiz:     computed,
      canFinish:        computed,

      sortSteps:        action,
      rearrangeStep:    action,
      addStep:          action,
      addChapter:       action,
      removeChapter:    action,
      removeStep:       action,
      setCurStep:       action,
      next:             action,
      back:             action,
      addDocument:      action,
      removeDocument:   action,
      sortProgress:     action,
      _setRating:       action,
      _setComment:      action
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);
  }

  /*******************************************************************************************************************************************
   * Database Access
   ********************************************************************************************************************************************/
  /**
   * Load this course's steps with all related content, i.e. quizzes, tasks and choices and videos. If the 
   * current user is an editor, this will load all quiz content.
   * If the current user is a participant or manager, this will generate
   * quizzes and only transmit data relevant for taking the quiz. 
   * 
   * @return <true> if quiz generation/loading was successful, false otherwise. 
   */
  public loadSteps = async (): Promise<boolean> => {
    try {
      const _steps: Paginated<IStep> = await client.service(Services.Step).find({
        query: { course_id: this.id }
      });
      // Check session storage if there is a last step stored for this course
      let cur_step_id:            number;
      let rating:                 number = null;
      let comment:                string = null;
      let rating_last_updated_at: Date = null;
      try {
        const last_steps: {[course_id: number]: number} = 
          sessionStorage.getItem(Course.last_step_session_storage_key) && 
          JSON.parse(sessionStorage.getItem(Course.last_step_session_storage_key));

        if(last_steps && last_steps[this.id]) cur_step_id = last_steps[this.id];
      } catch (err) {
        console.error("Failed to read session storage", err);
      }

      let progress: Paginated<IProgress>;
      if(rootStore.authStore.getCurUserType !== UserType.Editor) {
        progress = await client.service(Services.Progress).find({
          query: { course_id: this.id }
        });
        // Load the course user's rating and comment
        const user = authStore.getCurUser;
        const course_user: Paginated<ICourseUser> = await client.service(Services.CourseUser).find({
          query: {
            $select: ["rating", "comment", "rating_last_updated_at"],
            user_id: user.id,
            course_id: this.id,
          }
        });
        rating = course_user.data[0].rating;
        comment = course_user.data[0].comment;
        rating_last_updated_at = course_user.data[0].rating_last_updated_at;
      }

      // Sort the step list and set the current step
      runInAction(() => {

        this.sortSteps((_steps.data || []).map(s => Step.create(s, this)));

        this.course_progress = progress?.data || [];
        this.sortProgress();

        if(cur_step_id && this.steps.length && this.getStepIndex(cur_step_id) <= (this.getNextMandatoryStepIndex)) {
          this.setCurStep(this.steps.find(s => Number(s.id) === Number(cur_step_id)));
          if(rating)
            this._setRating(rating);
          if(comment)
            this._setComment(comment);
          if(rating_last_updated_at)
            this._setRatingUpdatedAt(rating_last_updated_at);
        } else {
          this.setCurStep(this.steps.length ? this.steps[0] : null);
        }
        this.steps_loaded = true;
      });
      return true;
    } catch (err) {
      console.error("Failed to load course steps", err);
      return false;
    }
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  /**
   * @param id      if provided, only loads the row with @id 
   * @param catalog if true, loads from the catalog service rather than restricting to content with content_user access
   * @param parent  if provided, limits to content that is a child of @parent
   */
  public static get = async (id?: number, catalog?: boolean, parent?: Curriculum<ICurriculum>, company_id?: number): Promise<Course | Course[]> => {
    try {
      if(parent?.data_type === DataType.Course) throw new Error("Incorrect parent content type");
      if(catalog) {
        const courses: Paginated<ICourse> = await client.service(Services.Catalog).find({query: {
          type: DataType.Course,
          id:   id
        }});
        const mapped_courses = courses.data.map(c => Course.create(c));
        return id ? mapped_courses[0] : mapped_courses;
      } else {
        if(id) {
          return Course.create(await client.service(Services.Course).get(id));
        } else {
          routingStore.startLoading();
          let course_ids: number[] = undefined;
          if(parent?.data_type === DataType.Package) {
            const programs: Paginated<IProgram> = await client.service(Services.Program).find({
              query: {package_id: parent.id, $select: ["id"]}
            });
            const progam_courses: Paginated<IProgramCourse> = await client.service(Services.ProgramCourse).find({
              query: {$select: ["course_id"], program_id: {$in: programs.data.map(p => p.id)}}
            });
            course_ids = progam_courses.data.map(pc => pc.course_id);
          }
          else if(parent?.data_type === DataType.Program) {
            const progam_courses: Paginated<IProgramCourse> = await client.service(Services.ProgramCourse).find({
              query: {$select: ["course_id"], program_id: parent.id}
            });
            course_ids = progam_courses.data.map(pc => pc.course_id);
          }
          const courses: Paginated<ICourse> = await client.service(Services.Course).find({
            query: course_ids ? {id: {$in: course_ids}} : undefined
          });
          routingStore.finishLoading();
          if(!courses.total) return [];
          return courses.data.map(u => Course.create(u));
        }
      }
    } catch (err) {
      routingStore.finishLoading();
      console.error("Failed to load Courses", err);
      return null;
    }
  }

  public static create(data: ICourse): Course {
    return new Course(data);
  }

  protected fromData(data: ICourse): void {
    super.fromData(data);
    this.category   = data.category;
    this.athen_id   = data.athen_id;
    this.notes      = data.notes;
  }

  public toData(): any {
    return {
      ...super.toData(),
      category:   this.category   || null,
      athen_id:   this.athen_id   || null,
      notes:      this.notes      || null,
    };
  }

  public toPatchData(patch: Partial<ICourse>): any {
    return {
      ...super.toPatchData(patch),
      category:   "category" in patch ?   this.category:   undefined,
      athen_id:   "athen_id" in patch ?   this.athen_id:   undefined,
      notes:      "notes" in patch ?      this.notes:      undefined,
    };
  }

  public get canEdit(): boolean {
    return authStore.getCurUserType === UserType.Editor;
  }

  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/
  public get getChapters(): Step[] {
    return this.steps.filter(s => !s.parent_id) || [];
  }

  public getStepsByChapter = computedFn((chapter_id: number): Step[] => {
    return this.steps.filter(s => s.parent_id === chapter_id && !s.isSub) || [];
  });

  private _getStepsByChapter = (chapter_id: number) => {
    return this.steps.filter(s => s.parent_id === chapter_id) || [];
  }

  public get getDocuments(): File[] {
    return (this.documents||[]).slice();
  }

  /**
   * @return an object that maps step ids to step objects
   */
  public get getStepMap(): {[id: number]: Step} {
    return this.steps.reduce((res: {[id: number]: Step}, val) => {res[val.id] = val; return res}, {});
  }
  
  public get getCurStep(): Step {
    return this.curStep;
  }

  /**
   * @return the index within @this.steps of the next mandatory step, counting from the 
   *         @getLatestStepIndex .
   *         Returns -1 if 
   *          - steps have not been loaded 
   *          - there are no steps (when first creating a course)
   *         and returns @this.steps.length if 
   *          - there are no mandatory steps.
   */
  public get getNextMandatoryStepIndex(): number {
    if(!this.steps || !this.course_progress) return -1;
    
    for(let i = 0; i < this.steps.length; i++) {
      if(this.steps[i].mandatory && !this.steps[i].isCompleted) return i;
    }
    return this.steps.length;
  }

  public getStepIndex = (step_id: number): number => {
    return this.steps.findIndex(s => s.id === step_id);
  }

  /**
   * @return progress percentage as integer of the current course
   */
  public get getProgress() {
    return this.progress || 0;
  }

  /**
   * @return true if the course can be published, i.e. all quizzes are valid.
   */
  public get canPublish(): boolean {
    if(!this.steps) return false;
    return !this.steps.some(s => s.quiz && !s.quiz.isValid);
  }

  public get hasFinalQuiz(): boolean {
    return Boolean(this.steps?.filter(s => s.isQuiz && s.quiz?.final)?.length >= 1);
  }

  public get canFinish(): boolean {
    return !this.steps.filter(s => s.mandatory).some(s => !s.isCompleted);
  }

  public get hasRating(): boolean {
    return this.rating !== null;
  }

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/
  setCurStep(step: Step) {
    if(step) {
      try {
        const last_steps: {[course_id: number]: number} = 
        sessionStorage.getItem(Course.last_step_session_storage_key) ?
        JSON.parse(sessionStorage.getItem(Course.last_step_session_storage_key))
        : {};
        
        last_steps[this.id] = step.id;
        sessionStorage.setItem(Course.last_step_session_storage_key, JSON.stringify(last_steps));
      } catch (err) {
        console.error("Failed to access session storage", err);
      }
    }

    this.curStep = step;
    if(step) step.loadDocuments();
  }

  /**
   * Add document take a dropped file and uploads it.
   * After a complete upload to the storage. The course also connects it to
   * Does not affect athen_id, thus, links to the shop are not(!) copied.
   */
  addDocument = async (document:any): Promise<any> => {
    try {
      document.course_id = this.id;
      const res = await document.upload();
      runInAction(() => this.documents = [...this.getDocuments, document]);
      return;
    } catch (err) {
      console.error("Failed to upload document to course", err);
      return null;
    }
  }

  removeDocument = async (document: File): Promise<any> => {
    try {
      const res = await document.remove();
      this.documents = this.documents.filter( f => f.id != document.id );
      return;
    } catch (err) {
      console.error("Failed to remove document from step", err);
      return null;
    }
  }

  _setRating(rating: number){
    this.rating = rating;
  }

  _setRatingUpdatedAt(ratingUpdatedDate: Date){
    this.rating_last_updated_at = ratingUpdatedDate;
  }

  _setComment(comment: string){
    this.comment = comment;
  }

  setRating = async (rating: number): Promise<any> => {
    this._setRating(rating);
    const currentDate = new Date();
    this._setRatingUpdatedAt(currentDate);
    const user = authStore.getCurUser;
    const course_user: Paginated<ICourseUser> = await client.service(Services.CourseUser).find({
      query: {
        $select: ["id"],
        user_id: user.id,
        course_id: this.id,
      }
    });
    await client.service(Services.CourseUser).patch(course_user.data[0].id, {
      rating: rating,
      rating_last_updated_at: currentDate.toISOString()
    });
  }
  
  setComment = async (comment: string): Promise<any> => {
    this._setComment(comment);
    const user = authStore.getCurUser;
    const course_user: Paginated<ICourseUser> = await client.service(Services.CourseUser).find({
      query: {
        $select: ["id"],
        user_id: user.id,
        course_id: this.id,
      }
    });
    await client.service(Services.CourseUser).patch(course_user.data[0].id, {
      comment: comment
    });
  }

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/
  /**
   * Ensure that steps in @this.steps are in canonical tree order 
   */
  sortSteps = (steps: IStep[]) => {
    const sortedSteps = [];
    const _steps: IStep[] = steps || this.steps || [];
    if(!_steps.length) return;
    let step = _steps.find(s => !s.parent_id && !s.prev_id);

    if(!step) {
      console.error("Incorret tree. Could not find start");
      return;
    }
    const index: {[id: number]: IStep} = _steps.reduce((res: {[id: number]: IStep}, val) => {
      res[val.id] = val;
      return res;
    }, {});

    const getParentNext = (step: IStep): IStep | undefined => {
      if(!step.parent_id) return undefined;
      if(index[step.parent_id].next_id) return index[index[step.parent_id].next_id];
      return getParentNext(index[step.parent_id]);
    }
    while(step) {
      sortedSteps.push(step);
      const child       = _steps.find(s => s.parent_id === step.id && !s.prev_id && !s.is_sub);
      const next        = step.next_id   && index[step.next_id];
      const parent_next = getParentNext(step);
      step = child || next || parent_next;
    }
    this.steps = [...sortedSteps, ..._steps.filter(s => s.is_sub)].map(s => Step.create(s, this));
  }

  addStep = async (chapter_id: number, stepType: StepType, is_sub?: boolean): Promise<Step> => {

    if(!this.steps_loaded) return;

    const steps     = this._getStepsByChapter(chapter_id).filter(s => !s.isSub);
    const last_step = steps.length ? steps[steps.length-1] : null;

    const new_step  = Step.create({
      content:   "<p></p>",
      course_id: this.id,
      parent_id: chapter_id,
      mandatory: false,
      title:     i18next.t(stepType),
      name:      i18next.t(stepType),
      is_sub:    is_sub
    }, this);

    new_step.prev_id = last_step?.id;
    const new_idx = this.steps.findIndex(s => s.id === (last_step?.id || chapter_id));
    
    // Update the next_id of the previously last step locally if necessary
    // No patch/update is needed, the backend will update the database 
    const _s = await new_step.save();
    if(!_s) {
      console.error("Failed to add chapter");
      return;
    }

    if(stepType === StepType.Quiz)  await new_step.makeQuiz();
    if(stepType === StepType.Video) await new_step.makeVideo();
    if(stepType === StepType.Grid)  await new_step.makeGrid();

    runInAction(() => {
      if(last_step) last_step.next_id = Number(_s.id);
      this.steps.splice(new_idx+1, 0, new_step);
    });

    return new_step;
  }
    
  addChapter = async (stepType: StepType, data?: Partial<IStep>): Promise<Step> => {
    if(!this.steps_loaded) return;

    const new_chap = Step.create({
      course_id: this.id,
      content:   data?.content   || "<p></p>",
      mandatory: data?.mandatory || false,
      title:     data?.title || i18next.t("chapter"),
      name:      data?.name  || i18next.t("chapter"),
    }, this);
    const last_chap = this.getChapters.pop();

    new_chap.prev_id = last_chap?.id;
    // Update the next_id of the previously last step locally if necessary
    // No patch/update is needed, the backend will update the database 
    const _c = await new_chap.save();
    if(!_c) {
      console.error("Failed to add chapter");
      return;
    }
    
    if(stepType === StepType.Quiz)  await new_chap.makeQuiz();
    if(stepType === StepType.Video) await new_chap.makeVideo();
    if(stepType === StepType.Grid)  await new_chap.makeGrid();

    runInAction(() => {
      this.steps = [...this.steps, new_chap]
      if(last_chap) last_chap.next_id = _c.id;
    });
    return new_chap;
  }

  removeChapter = async (chapter_id: number) => {
    const step: Step = this.getStepMap[chapter_id];
    if(!step) return;

    if(!(await step.remove())) {
      console.error("Failed to remove step");
    } else {
      runInAction(() => {
        if(step.prev_id) {
          const prev_step: Step = this.getStepMap[step.prev_id];
          prev_step.next_id = step.next_id;
        }
        if(step.next_id) {
          const next_step: Step = this.getStepMap[step.next_id];
          next_step.prev_id = step.prev_id;
        }
        this.steps = this.steps.filter(s => s.parent_id !== chapter_id && s.id !== chapter_id);
      });
    }
  }

  removeStep = async (step_id: number): Promise<boolean> => {
    const step: Step = this.getStepMap[step_id];
    if(!step) return false;

    try {
      if(!(await step.remove())) {
        console.error("Failed to remove step");
        return false;
      } else {
        runInAction(() => {
          if(step.prev_id) {
            const prev_step: Step = this.getStepMap[step.prev_id];
            prev_step.next_id = step.next_id;
          }
          if(step.next_id) {
            const next_step: Step = this.getStepMap[step.next_id];
            next_step.prev_id = step.prev_id;
          }
          this.steps = this.steps.filter(s => s.id !== step_id);
        });
        return true;
      }
    } catch(err) {
      console.error("Failed to remove step", err);
      return false;
    }
  }

  /**
   * Rearranges the doubly linked list of steps. Takes care of assigning the corresponding 
   * prev_id and next_ids. 
   * 
   * Note: @new_idx is assumed relative to its parent. That is, a chapter idx is relative only 
   * to other chapters and a step idx is relative only to other steps in that chapter. 
   * 
   * @param chapter_id Id of the chapter of the steps to be rearraned. If null, chapters will be rearranged instead. 
   * @param old_idx Index of the step that should be moved, relative to its parent.
   * @param new_idx Destination index relative to its parent.
   */
  rearrangeStep = async (chapter_id: number, old_idx: number, new_idx: number): Promise<void> => {
    const old_steps = [...this.steps.filter(s => !s.isSub)];
    const cpy       = [...this.steps.filter(s => !s.isSub)];
    const subs      = [...this.steps.filter(s => s.isSub)];

    if(chapter_id) {
      // Note that the indices ignore sub steps so they need to be accounted for separately
      const steps = old_steps.filter(s => s.parent_id === chapter_id);
      steps.splice(new_idx, 0, ...steps.splice(old_idx, 1));
      const start = old_steps.findIndex(s => s.id === chapter_id);
      cpy.splice(start+1, steps.length, ...steps);
    } else {
      const old_c = this.steps.findIndex(s => s.id === this.getChapters[old_idx].id);
      const new_c = this.steps.findIndex(s => s.id === this.getChapters[new_idx].id);
      cpy.splice(new_c, 0, ...cpy.splice(old_c, 1));
    }
    // Rearrange the local array to avoid UI glitches
    this.steps = [...cpy, ...subs];

    try {
      // This will hold either the arranged steps of @chapter_id or all chapters of @course_id
      const new_steps: IStep[] = await this.service.rearrangeSteps({ 
        course_id: this.id,
        chapter_id,
        old_idx,
        new_idx,
      });
      if(chapter_id) {
        const start = this.steps.findIndex(s => s.id === chapter_id);
        runInAction(() => this.steps.splice(start+1, new_steps.length, ...new_steps.map(s =>Step.create(s, this))));
      } else this.loadSteps();
    } catch (err) {
      console.error("Failed to rearrange steps", err);
      runInAction(() => this.steps = old_steps);
    }
  }

  /**
   * Clones the course with all related content. 
   * Does not affect course id, thus, course access is not(!) copied.
   * Does not affect athen_id, thus, links to the shop are not(!) copied.
   */
  clone = async (): Promise<Course> => {
    const clone: ICourse = this.toData();
    clone.title += "_clone";
    clone.status = DataStatus.Draft;
    delete clone.athen_id;
    delete clone.id;

    try {
      const res = await this.service.create(clone, paramsForServer({clone: this.id}));
      return Course.create(res);
    } catch (err) {
      console.error("Failed to clone course", err);
      return null;
    }
  }


  /**
   * Publish a draft course. This cannot be undone! 
   * Only possible if all contained quizzes are valid. 
   * 
   * @return <true> if succesful 
   */
  publish = async (): Promise<boolean> => {
    // Check if all quizzes in the course are valid:
    if(this.steps.some(s => s.isQuiz && !s.quiz.isValid)) {
      console.error("The course contains invalid quizzes");
      return false;
    }
    if(this.steps.filter(s => s.isQuiz && s.quiz.final).length !== 1) {
      console.error("The course does not contain a final quiz");
      return false;
    }
    
    try {
      return await this.set("status", DataStatus.Published, true);
    } catch(err) {
      console.error("Failed to publish course", err);
      return false;
    }
  }

  public loadDocuments = async (includeAllSteps = false) => {
    if(this.getDocuments.length) return;

    try {
      const query = {
        query: {
            course_id: this.id,
          }
      };
      const result: Paginated<IFile> = await client.service(Services.Document).find(query);

      runInAction(() => {
        this.documents = [];
        result.data.map( fd => this.documents.push( File.create(fd) ));
      });
      return true;
    } catch (err) {
      console.error("Failed to load documents for step", err);
      return false;
    }
  }

  /**
   * Sets @this.course.curStep to the next step in the step structure if possible. 
   * Takes care of recording progress.
   * 
   * @return <true> if successful or if the end of the course was reached, <false> if
   *         recording progress failed. 
   */
  public next = async (preview?: boolean): Promise<boolean> => {
    if(preview && !this.getCurStep.isQuiz) return false;

    // Not a preview but a quiz
    if(this.getCurStep.isQuiz) {
      const quiz = this.getCurStep.quiz;
      if(
        quiz.is_control_quiz && 
        !quiz.getCurTask.showAnswer
      ) {
        quiz.getCurTask.setShowAnswer(true);
        return true;
      }

      if(preview && quiz.isCompleted) {
        routingStore.startLoading();
        await this.getCurStep.loadQuiz(true);
        quiz.reset();
        routingStore.finishLoading();
        return true;
      }

      // Quiz was completed in the past and the user comes back to this step
      if(this.curStep.isCompleted && !this.getCurStep.quiz.is_control_quiz) {
        if(this.getCurStep.isLast) {
          routingStore.startLoading();
          await this.finishCourse();
          this.setCurStep(null);
          routingStore.push(Pages.Library);
          routingStore.finishLoading();
          return true;
        }
        this.setCurStep(this.curStep.getNextStep);
        if(this.curStep && !this.curStep.content) return this.next();
        return true;
      }

      // Quiz was not completed in the past so the user is taking it right now

      // User successfully validated the quiz and can now continue
      if(quiz.correct_answers === quiz.num_correct) {
        if(this.getCurStep.isLast) {
          routingStore.startLoading();
          await this.finishCourse();
          this.setCurStep(null);
          routingStore.push(Pages.Library);
          routingStore.startLoading();
          return true;
        }
        this.setCurStep(this.curStep.getNextStep);
        if(this.curStep && !this.curStep.content) return this.next();
        return true;
        // return await this.finishCourse();
      }

      // Quiz was validated but user failed
      if(quiz.correct_answers > -1) {
        quiz.reset();
        if(quiz.is_control_quiz) {
          this.setCurStep(this.curStep.getNextStep);
          return true;
        }
        routingStore.startLoading();
        const loadQuizResult = await this.curStep.loadQuiz();
        routingStore.finishLoading();
        return loadQuizResult;
      }

      // Quiz not yet validated but all answers were given
      if(quiz.isCompleted) { // If all tasks were answered
        routingStore.startLoading();
        const validationResults = await quiz.validate();
        if(!quiz.showResults && !this.getCurStep.isLast && !quiz.final){
          this.setCurStep(this.curStep.getNextStep);
        }
        routingStore.finishLoading();
        return validationResults;
      }
      
      if(!quiz.isCompleted) {
        // Quiz not yet validated but and more tasks remain
        this.curStep.quiz.nextTask();
        return true;
      }
    } 

    // Not a quiz or a CQQ that is completed
    if(this.getCurStep.isLast) {
      routingStore.startLoading();
      await this.getCurStep.recordProgress();
      await this.finishCourse();
      this.setCurStep(null);
      routingStore.push(Pages.Library);
      routingStore.finishLoading();
      return true;
    }

    if(!this.curStep.isCompleted) {
      routingStore.startLoading();
      const recordProgressResult = await this.curStep.recordProgress()
      routingStore.finishLoading();
      if(!recordProgressResult) return false;
    }

    this.setCurStep(this.curStep.getNextStep);
    if(this.curStep && !this.curStep.content) return this.next();
    return true;
  }

  public back = (): void => {
    this.setCurStep(this.curStep.getPrevStep);
    if(this.curStep && (!this.curStep.content || this.curStep.isQuiz)) return this.back();
  }

  public sortProgress = (): void => {
    this.course_progress = this.course_progress.sort((p, q) => 
      this.getStepIndex(p.step_id) - this.getStepIndex(q.step_id)
    );
  }

  public finishCourse = async (): Promise<boolean> => {
    try {
      // CompleteCourse only when the course is not completed
      if(!this.isCompleted)
        return await client.service(Services.Progress).completeCourse(this.id);
      return true;
    } catch(err) {
      console.error("Failed to complete course", err);
      return false;
    }
  }
}
